import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authService } from '../services';
import { useAuthStore } from '../store/auth';
import { messageHandler } from '../services/message-handler.service';
import { config } from '../lib/config';
import RegisterForm from '../components/register/RegisterForm';
import type { RegisterRequest } from '../types/api';
import api from '../lib/api';

function Register() {
  const [isLoading, setIsLoading] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [inputAffiliateCode, setInputAffiliateCode] = useState<string>('');
  const [isApplyingCode, setIsApplyingCode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore(state => state.login);

  // Extract affiliate code from URL if present
  useEffect(() => {
    // Check for affiliate code in query parameters
    const params = new URLSearchParams(location.search);
    const code = params.get('ref');
    if (code) {
      setAffiliateCode(code);
      setInputAffiliateCode(code);
    }
  }, [location]);

  // Fetch affiliate info if code is present
  const {
    data: affiliateInfo,
    refetch: refetchAffiliateInfo,
    isError: isAffiliateError,
  } = useQuery({
    queryKey: ['affiliate', affiliateCode],
    queryFn: () => authService.getAffiliateInfo(affiliateCode!),
    enabled: !!affiliateCode,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: () => {
      // If affiliate code is invalid, clear it
      if (affiliateCode) {
        messageHandler.warning('Código de afiliado inválido');
      }
    },
  });

  const createSubscriptionMutation = useMutation({
    mutationFn: async (data: { planId: number; affiliateCode?: string }) => {
      return api.post('/subscriptions/users', {
        plan_id: data.planId,
        is_trial: false,
        affiliate_code: data.affiliateCode,
      });
    },
    onSuccess: () => {
      navigate('/', { replace: true });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || 'Erro ao criar assinatura';
      messageHandler.error(errorMessage);
      // Still navigate to dashboard even if subscription creation fails
      navigate('/', { replace: true });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterRequest) => {
      return authService.register(data);
    },
    onSuccess: async (response, data) => {
      if (response?.success) {
        messageHandler.success('Cadastro realizado com sucesso!');

        // Auto login after successful registration
        try {
          await login(data.username, data.password);

          // After successful login, create subscription
          if (data.plan_id) {
            createSubscriptionMutation.mutate({
              planId: data.plan_id,
              affiliateCode: data.affiliate_code,
            });
          } else {
            navigate('/', { replace: true });
          }
        } catch (error) {
          // If auto-login fails, redirect to login page
          navigate('/login');
        }
      } else {
        messageHandler.error(response?.error || response?.message || 'Erro ao realizar cadastro');
        setIsLoading(false);
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error || error?.message || 'Erro ao realizar cadastro';
      messageHandler.error(errorMessage);
      setIsLoading(false);
    },
  });

  const handleSubmit = async (data: RegisterRequest) => {
    setIsLoading(true);

    // Add affiliate code if available
    if (affiliateCode) {
      data.affiliate_code = affiliateCode;
    }

    registerMutation.mutate(data);
  };

  const handleApplyAffiliateCode = async () => {
    if (!inputAffiliateCode.trim()) {
      messageHandler.warning('Por favor, informe um código de afiliado');
      return;
    }

    setIsApplyingCode(true);
    setAffiliateCode(inputAffiliateCode.trim());

    try {
      await refetchAffiliateInfo();
    } finally {
      setIsApplyingCode(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-600 dark:bg-gray-900 p-4">
      <div className="max-w-4xl w-full bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
        <div className="text-center mb-6">
          <img className="mx-auto h-16 w-auto" src="/logo.png" alt={config.name} />
          <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Crie sua conta</h2>

          {affiliateInfo && affiliateInfo.success && (
            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/30 rounded-md">
              <p className="text-sm text-green-700 dark:text-green-300">
                Você foi convidado por <strong>{affiliateInfo.data.name}</strong>!
                {affiliateInfo.data.affiliate_discount > 0 && (
                  <span>
                    {' '}
                    Você receberá <strong>{affiliateInfo.data.affiliate_discount}%</strong> de
                    desconto.
                  </span>
                )}
              </p>
            </div>
          )}

          {isAffiliateError && affiliateCode && (
            <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/30 rounded-md">
              <p className="text-sm text-red-700 dark:text-red-300">
                Código de afiliado inválido. Por favor, verifique e tente novamente.
              </p>
            </div>
          )}
        </div>

        <RegisterForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          affiliateCode={affiliateCode}
          affiliateDiscount={affiliateInfo?.data?.affiliate_discount}
          inputAffiliateCode={inputAffiliateCode}
          onInputAffiliateCodeChange={setInputAffiliateCode}
          onApplyAffiliateCode={handleApplyAffiliateCode}
          isApplyingCode={isApplyingCode}
        />

        <div className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
          <p>{config.company.copyright}</p>
          <p className="mt-1">Versão {config.version}</p>
        </div>
      </div>
    </div>
  );
}

export default Register;
